import { Fragment, useState } from 'react'
import { Check, ChevronRight, ListFilter, Search } from 'lucide-react'

import type { DataGridColumnSchema } from '../lib/types'
import { cn } from '@/lib/utils'
import { Input } from '@/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'

import { DataGridCategoryFilter } from './data-grid-category-filter'
import { useGridContext } from './data-grid-context'
import type { DataGridFilterPresentation } from './data-grid-filter-panel'
import { DataGridFilterIcon } from './data-grid-filter-shared'
import { DataGridTimestampFilter } from './data-grid-timestamp-filter'
import {
  DataGridTextFilter,
  DataGridValueFilter,
} from './data-grid-value-filters'

export function DataGridFilters({ column: onlyColumn }: { column?: string } = {}) {
  const { grid } = useGridContext()
  const filterable = grid.schema.columns.filter((column) =>
    column.filterable
      && (!onlyColumn || column.id === onlyColumn))

  if (onlyColumn) {
    return filterable.map((column) => (
      <DataGridFilterControl key={column.id} column={column} presentation="chip" />
    ))
  }

  const activeColumns = filterable.filter((column) =>
    grid.criteria.filter?.some((filter) => filter.column === column.id))

  return (
    <>
      <DataGridAddFilter columns={filterable} />
      {activeColumns.map((column) => (
        <DataGridFilterControl key={column.id} column={column} presentation="chip" />
      ))}
    </>
  )
}

export function DataGridActiveFilters() {
  const { grid } = useGridContext()
  const activeColumns = grid.schema.columns.filter((column) =>
    column.filterable
      && grid.criteria.filter?.some((filter) => filter.column === column.id))

  return activeColumns.map((column) => (
    <DataGridFilterControl key={column.id} column={column} presentation="chip" />
  ))
}

function DataGridAddFilter({ columns }: { columns: readonly DataGridColumnSchema[] }) {
  const { grid } = useGridContext()
  const [open, setOpen] = useState(false)
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const selectedColumn = columns.find((column) => column.id === selectedColumnId)
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const visibleColumns = normalizedQuery
    ? columns.filter((column) => [column.label, column.id].some((candidate) =>
        candidate.toLocaleLowerCase().includes(normalizedQuery)))
    : columns

  function changeOpen(next: boolean) {
    if (next) {
      setSelectedColumnId(null)
      setQuery('')
    }
    setOpen(next)
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger
        aria-label="Add filter"
        className="border-input text-muted-foreground hover:border-muted-foreground/40 hover:bg-accent hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/30 inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border px-2 text-xs font-medium outline-hidden transition-colors focus-visible:ring-2"
      >
        <ListFilter aria-hidden className="size-3.5" />
        Add filter
      </PopoverTrigger>
      {selectedColumn ? (
        <DataGridFilterControl
          column={selectedColumn}
          presentation="content"
          open
          onBack={() => setSelectedColumnId(null)}
          onApplied={() => setOpen(false)}
        />
      ) : (
        <PopoverContent align="end" className="w-64 gap-0 overflow-hidden p-0">
          <div className="border-border border-b p-1.5">
            <div className="relative">
              <Search
                aria-hidden
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2"
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a column…"
                aria-label="Find a filter column"
                className="bg-muted/60 h-8 border-0 pr-2 pl-7 text-xs shadow-none"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {visibleColumns.length === 0 ? (
              <p className="text-muted-foreground px-2 py-5 text-center text-xs">
                No columns match.
              </p>
            ) : visibleColumns.map((column) => {
              const active = grid.criteria.filter?.some((filter) =>
                filter.column === column.id) ?? false
              return (
                <button
                  key={column.id}
                  type="button"
                  onClick={() => {
                    if (column.kind === 'category' && column.operators.includes('in')) {
                      void grid.loadFacetOptions(column.id)
                    }
                    setSelectedColumnId(column.id)
                    setQuery('')
                  }}
                  className={cn(
                    'group/field text-foreground focus-visible:bg-accent hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs outline-hidden',
                    active && 'bg-muted/60',
                  )}
                >
                  <DataGridFilterIcon
                    kind={column.kind}
                    className="text-muted-foreground size-3.5 shrink-0"
                  />
                  <span className="min-w-0 flex-1 truncate">{column.label}</span>
                  {active ? (
                    <span className="text-foreground flex shrink-0 items-center gap-1 text-[10px] font-medium">
                      <Check aria-hidden className="size-3" />
                      Active
                    </span>
                  ) : null}
                  <ChevronRight
                    aria-hidden
                    className="text-muted-foreground/70 size-3.5 shrink-0 transition-transform group-hover/field:translate-x-0.5 motion-reduce:transition-none"
                  />
                </button>
              )
            })}
          </div>
        </PopoverContent>
      )}
    </Popover>
  )
}

export function DataGridFilterControl({
  column,
  presentation,
  open,
  onBack,
  onApplied,
}: {
  column: DataGridColumnSchema
  presentation: DataGridFilterPresentation
  open?: boolean
  onBack?: () => void
  onApplied?: () => void
}) {
  const { grid, renderers } = useGridContext()
  const facetLoad = grid.facetState[column.id]
  const facet = facetLoad?.facet ?? grid.facets[column.id]
  const value = grid.criteria.filter?.find((filter) => filter.column === column.id)
  const custom = renderers?.[column.id]?.filter
  const controlKey = (column.kind === 'category' || column.kind === 'timestamp') && !custom
    ? column.id
    : `${column.id}:${open ? 'open' : 'closed'}:${JSON.stringify(value ?? null)}`

  if (custom) {
    return (
      <Fragment key={controlKey}>
        {custom({
          column,
          value,
          facet,
          operators: column.operators,
          options: facet?.values,
          optionsLoading: facetLoad?.isLoading ?? false,
          optionsError: facetLoad?.error ?? null,
          optionSearch: facetLoad?.search ?? '',
          setOptionSearch: (next) => void grid.loadFacetOptions(column.id, next),
          loadMoreOptions: () => void grid.loadFacetOptions(
            column.id,
            facetLoad?.search ?? '',
            true,
          ),
          timestampRange: { minimum: facet?.minimum, maximum: facet?.maximum },
          presentation,
          open,
          onBack,
          onApplied,
          grid,
        })}
      </Fragment>
    )
  }

  const props = { column, presentation, open, onBack, onApplied }
  if (column.kind === 'text') return <DataGridTextFilter key={controlKey} {...props} />
  if (column.kind === 'category') return <DataGridCategoryFilter key={controlKey} {...props} />
  if (column.kind === 'timestamp') return <DataGridTimestampFilter key={controlKey} {...props} />
  return <DataGridValueFilter key={controlKey} {...props} />
}

export function DataGridFilter({ column }: { column: string }) {
  return <DataGridFilters column={column} />
}
